import { useEffect, useRef } from 'react'
import { Link } from '@tanstack/react-router'
import { mdiDeleteOutline, mdiPencilOutline } from '@mdi/js'
import { useTranslation } from 'react-i18next'
import { Button } from '../ui/button'
import { AppIcon } from '../ui/icon'
import { DeleteConfirmDialog } from '../ui/delete-confirm-dialog'
import { PageVersionHistoryButton, formatRelativeTime } from '../PageVersionHistory'
import { KindBadge } from './KindBadge'
import type { KnowledgeNote } from '../../hooks/useKnowledge'

interface NoteCardProps {
    org: string
    note: KnowledgeNote
    canWrite: boolean
    onEdit?: () => void
    onDelete?: () => void
    deleting?: boolean
    /** Asset notes keep a version history (the asset the note belongs to). */
    historyAssetUid?: string
    /** Show where the note lives (the organization page lists asset notes too). */
    showSource?: boolean
    /** The note a link pointed at: scrolled into view and highlighted. */
    highlighted?: boolean
}

export function NoteCard({ org, note, canWrite, onEdit, onDelete, deleting, historyAssetUid, showSource, highlighted }: NoteCardProps) {
    const { t } = useTranslation('knowledge')
    const ref = useRef<HTMLLIElement>(null)
    useEffect(() => {
        if (highlighted) ref.current?.scrollIntoView({ block: 'center', behavior: 'smooth' })
    }, [highlighted])
    const isAssetNote = note.source.type === 'asset'
    const hasHistory = note.updated_at !== note.created_at

    return (
        <li
            ref={ref}
            className={`rounded-md border bg-card p-3 ${highlighted ? 'border-primary ring-2 ring-primary/40' : 'border-border'}`}
            data-testid={`note-${note.uid}`}
        >
            <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="flex flex-wrap items-center gap-2">
                    <h4 className="font-medium text-foreground" data-testid="note-title">
                        {note.title}
                    </h4>
                    <KindBadge kind={note.kind} />
                    {highlighted && (
                        <span className="text-xs font-medium text-primary" data-testid="note-card-highlighted">
                            {t('linked_note')}
                        </span>
                    )}
                    {isAssetNote && !note.applies_to_subtree && (
                        <span className="text-xs text-muted-foreground" data-testid="note-only-here">
                            {t('only_here')}
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-1">
                    {historyAssetUid && (
                        <PageVersionHistoryButton
                            assetUid={historyAssetUid}
                            enabled={hasHistory}
                            note={{ org, noteUid: note.uid }}
                            testId={`note-history-${note.uid}`}
                            livePage={{ content: note.content, text_content: note.text_content }}
                        />
                    )}
                    {canWrite && onEdit && (
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={onEdit}
                            aria-label={t('edit')}
                            title={t('edit')}
                            data-testid={`note-edit-${note.uid}`}
                        >
                            <AppIcon path={mdiPencilOutline} size="sm" />
                        </Button>
                    )}
                    {canWrite && onDelete && (
                        <DeleteConfirmDialog
                            title={t('delete_title')}
                            description={t('delete_description', { title: note.title ?? '' })}
                            onConfirm={onDelete}
                            isDeleting={deleting}
                            trigger={
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    aria-label={t('delete')}
                                    title={t('delete')}
                                    data-testid={`note-delete-${note.uid}`}
                                >
                                    <AppIcon path={mdiDeleteOutline} size="sm" />
                                </Button>
                            }
                        />
                    )}
                </div>
            </div>
            {note.text_content && (
                <p className="mt-2 whitespace-pre-wrap text-sm text-foreground" data-testid="note-body">
                    {note.text_content}
                </p>
            )}
            <div className="mt-2 flex flex-wrap gap-x-3 text-xs text-muted-foreground">
                <span data-testid="note-updated">{formatRelativeTime(note.updated_at)}</span>
                {showSource && (
                    isAssetNote && note.source.asset_uid ? (
                        <Link
                            to="/$org/assets/$assetUid"
                            params={{ org, assetUid: note.source.asset_uid }}
                            className="text-primary hover:underline"
                            data-testid={`note-source-${note.uid}`}
                        >
                            {t('source_asset', { name: note.source.asset_name ?? '' })}
                        </Link>
                    ) : (
                        <span data-testid={`note-source-${note.uid}`}>{t('source_organization')}</span>
                    )
                )}
            </div>
        </li>
    )
}
